import {
  Box,
  Button,
  Chip,
  Icon,
  Menu,
  MenuItem,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassScheduleRequest_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import {
  faCalendarCircleExclamation,
  faChevronRight,
  faGlobeRegular,
  faTimes,
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import { AvailableDateSelector } from "@outschool/ui-components-website";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import { groupBy, uniqBy } from "lodash";
import React from "react";

import { useTimeZone } from "../../providers/TimeZoneProvider";
import ClassScheduleRequest from "../ClassScheduleRequest";
import { AvailableTimesGrid } from "../detailPage/activityEnrollableTimes/ActivityEnrollableTimes";
import useFetchEnrollableTimes from "../detailPage/hooks/useFetchEnrollableTimes";
import { EnrollmentDurationSelect } from "./enrollmentForm/EnrollmentDurationSelect";
import { useFutureMeetings } from "./EnrollmentMeetingPurchaseDuration";
import { EnrollmentOrgTeacher, FutureMeetingInfo } from "./types";

type EnrollableTime =
  ActivityEnrollableTimesModal_ActivityFragment["enrollableTimes"][0];

export function ScheduleDateTimeSelector({
  activity,
  meetingStartTime,
  onChange,
  withHeader = false,
  isThreeStepBooking = false,
  orgTeacherUid,
  isPrepayEnrollment,
  givenDuration,
  onHandleClose,
  activityWithEnrollableTimes,
}: {
  activity: ClassScheduleRequest_ActivityFragment;
  meetingStartTime: Date;
  onChange: (
    startTime: Date,
    endTime?: Date,
    meetingDurationWeeks?: number | null,
    meetingTimes?: FutureMeetingInfo[],
    orgTeacher?: EnrollmentOrgTeacher,
  ) => void;
  withHeader?: boolean;
  isThreeStepBooking?: boolean;
  orgTeacherUid?: string;
  isPrepayEnrollment?: boolean;
  givenDuration?: null | number;
  onHandleClose?: (e?: React.SyntheticEvent) => void;
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
}) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\ScheduleDateTimeSelector",
  );
  const { timeZone } = useTimeZone();
  const trackEvent = useTrackEvent();
  const { currentUser, isLoggedIn } = useSession();

  // We may drill down to a specific teacher, keep track of all teacher enrollable times
  const [
    allTeacherActivityEnrollableTimes,
    setAllTeacherActivityEnrollableTimes,
  ] = React.useState<EnrollableTime[] | undefined>(undefined);

  const [activityEnrollableTimes, setActivityEnrollableTimes] = React.useState<
    EnrollableTime[] | undefined
  >(undefined);

  const [selectedDateTime, setSelectedDateTime] =
    React.useState<Date>(meetingStartTime);
  const [hasSelectedTime, setHasSelectedTime] = React.useState(false);

  const [loading, setLoading] = React.useState(true);
  const startCount =
    withHeader && isThreeStepBooking && !isPrepayEnrollment ? null : 1;

  const [enrollmentDuration, setEnrollmentDuration] = React.useState<
    null | number
  >(givenDuration === undefined ? startCount : givenDuration);

  const [selectedTeacher, setSelectedTeacher] = React.useState<string | null>(
    null,
  );

  const fetchEnrollableTimesAll = useFetchEnrollableTimes();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  React.useEffect(() => {
    if (selectedDateTime) {
      setEnrollmentDuration(
        givenDuration === undefined ? startCount : givenDuration,
      );
    }
  }, [selectedDateTime, setEnrollmentDuration, givenDuration, startCount]);

  const uniqueTeachers = React.useMemo(
    () =>
      uniqBy(
        allTeacherActivityEnrollableTimes?.map(time => {
          return {
            uid: time.teacher?.uid,
            name: time.teacher?.name,
          };
        }),
        "uid",
      ),
    [allTeacherActivityEnrollableTimes],
  );

  React.useEffect(() => {
    if (activityWithEnrollableTimes) {
      // Passed in Data comes from the CDP
      setAllTeacherActivityEnrollableTimes(
        activityWithEnrollableTimes.enrollableTimes,
      );
      setActivityEnrollableTimes(activityWithEnrollableTimes.enrollableTimes);
      setSelectedDateTime(
        dayjs(
          activityWithEnrollableTimes.enrollableTimes[0].startTime,
        ).toDate(),
      );
      setHasSelectedTime(true);
      setLoading(false);
    } else {
      // CLP does not load this data so we load it now
      fetchEnrollableTimesAll(activity.uid)
        .then(times => {
          setAllTeacherActivityEnrollableTimes(times.enrollableTimes);
          setActivityEnrollableTimes(times.enrollableTimes);
          if (times.enrollableTimes?.length > 0) {
            setSelectedDateTime(
              dayjs(times.enrollableTimes[0].startTime).toDate(),
            );
            setHasSelectedTime(true);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [activity.uid, fetchEnrollableTimesAll, activityWithEnrollableTimes]);

  React.useEffect(() => {
    if (!activityEnrollableTimes?.[0]) {
      return;
    }

    const selectedDateIsSameOrBeforeToday = dayjs(
      selectedDateTime,
    ).isSameOrBefore(dayjs());
    const selectedDateIsAvailable = activityEnrollableTimes.some(time =>
      dayjs(selectedDateTime).isSame(dayjs(time.startTime)),
    );

    if (selectedDateIsSameOrBeforeToday || !selectedDateIsAvailable) {
      // Jump to the first time the teacher is available
      setSelectedDateTime(
        dayjs(activityEnrollableTimes?.[0].startTime).toDate(),
      );
      setHasSelectedTime(true);
    }
  }, [selectedTeacher, selectedDateTime, activityEnrollableTimes]);

  React.useEffect(() => {
    // Filter enrollable times by selected teacher, if any
    if (selectedTeacher) {
      const filteredTimes = allTeacherActivityEnrollableTimes?.filter(
        time => time.teacher?.uid === selectedTeacher,
      );
      setActivityEnrollableTimes(filteredTimes);
    } else {
      setActivityEnrollableTimes(allTeacherActivityEnrollableTimes);
    }
  }, [selectedTeacher, allTeacherActivityEnrollableTimes]);

  const isMobile = useIsMobile();
  const useStickyFooter = isMobile && isThreeStepBooking;

  const startOfDateString = (date: Date) =>
    dayjs(date).tz(timeZone).startOf("date").toISOString();

  // There may be multiple teachers overlapping a time slot,
  // randomly select an enrollable time/teacher to keep distribution fair.
  const enrollableTimes = React.useMemo(() => {
    const groupedByStartTime = groupBy(activityEnrollableTimes, "startTime");
    return Object.values(groupedByStartTime).map(
      group => group[Math.floor(Math.random() * group.length)],
    );
  }, [activityEnrollableTimes]);

  const enrollableTimesByDate = groupBy(
    enrollableTimes.map(time => time.startTime),
    startOfDateString,
  );

  const availableDates = Object.keys(enrollableTimesByDate);
  const availableTimes =
    enrollableTimesByDate[startOfDateString(selectedDateTime)] || [];

  const startTime =
    isThreeStepBooking && hasSelectedTime
      ? dayjs(selectedDateTime).toDate()
      : undefined;
  const enrollableTime =
    startTime &&
    enrollableTimes?.find(time =>
      dayjs(dayjs(time.startTime).toDate()).isSame(startTime),
    );
  const endTime = startTime ? enrollableTime?.endTime : undefined;

  // when we are looking at a specific enrollment, lets keep track of the exact org teacher we're interacting with
  const hasOrgTeacher = !!enrollableTime?.teacher?.teacherForSellerOrg;

  const orgTeacher: EnrollmentOrgTeacher | undefined = hasOrgTeacher
    ? enrollableTime?.teacher
    : orgTeacherUid
    ? {
        uid: orgTeacherUid,
      }
    : undefined;

  const {
    duration,
    canScheduleContinuously,
    futureMeetingsLoading,
    futureMeetings,
    durationsWithAvailability,
  } = useFutureMeetings({
    activityUid: activity.uid,
    startTime: startTime as Date,
    endTime,
    enrollmentDuration,
    orgTeacherUid: orgTeacher?.uid,
  });

  if (withHeader) {
    const weeklyUntilCancel_OnClick = () => {
      const currentStartTime = dayjs(selectedDateTime).toISOString();
      const endTime = enrollableTimes?.find(time =>
        dayjs(dayjs(time.startTime).toISOString()).isSame(currentStartTime),
      )?.endTime;
      trackEvent("activity_enroll_click", {
        isLoggedIn,
        isThreeStepBooking,
        activityUid: activity.uid,
        enrollmentDuration: duration === null ? "UNTIL_I_CANCEL" : duration,
        orgTeacherUid: orgTeacher?.uid,
        meetingStartTime: selectedDateTime,
        meetingEndTime: endTime,
      });
      if (!isThreeStepBooking) {
        onChange(selectedDateTime, dayjs(endTime).toDate());
      } else {
        let enrollmentDuration_sessions = null;
        if (duration != null) {
          let count = 0;
          enrollmentDuration_sessions = 0;
          for (const fm of futureMeetings) {
            enrollmentDuration_sessions++;
            if (fm.isAvailable) {
              count++;
            }
            if (count === duration) {
              break;
            }
          }
        }
        onChange(
          selectedDateTime,
          dayjs(endTime).toDate(),
          enrollmentDuration_sessions,
          futureMeetings,
          orgTeacher,
        );
      }
    };
    return (
      <Box>
        {loading ? (
          <Loading />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Icon icon={faTimes} onClick={onHandleClose} />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 16,
                paddingBottom: 8,
              }}
            >
              {isThreeStepBooking ? (
                <Typography variant="h6">
                  {t("Select when you want to start meeting")}
                </Typography>
              ) : (
                <Typography variant="h6">{t("Available Times")}</Typography>
              )}
              <Chip
                label={timeZone}
                size="small"
                color={`neutral`}
                icon={<Icon icon={faGlobeRegular} />}
              />
            </Box>
            <Box>
              {isThreeStepBooking && uniqueTeachers.length > 1 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Box sx={{ position: "relative" }}>
                    <Button
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                        setAnchorEl(e.currentTarget)
                      }
                      endIcon={
                        <Icon
                          icon={faChevronRight}
                          sx={{
                            transform: Boolean(anchorEl)
                              ? "rotate(90deg)"
                              : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                      variant="outlined"
                      sx={(theme: Theme) => ({
                        paddingLeft: "16px",
                        paddingRight: "12px",
                        width: "316px",
                        justifyContent: "space-between",
                        overflow: "hidden",
                        [theme.breakpoints.down("md")]: {
                          width: "100%",
                        },
                      })}
                    >
                      {selectedTeacher
                        ? uniqueTeachers.find(t => t.uid === selectedTeacher)
                            ?.name
                        : t("Any teacher")}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setSelectedTeacher(null);
                          setAnchorEl(null);
                        }}
                        selected={!selectedTeacher}
                      >
                        {t("Any teacher")}
                      </MenuItem>
                      {uniqueTeachers.map(teacher => (
                        <MenuItem
                          key={teacher.uid}
                          onClick={() => {
                            setSelectedTeacher(teacher.uid);
                            setAnchorEl(null);
                          }}
                          selected={teacher.uid === selectedTeacher}
                        >
                          {teacher.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                </Box>
              )}
              <Box
                sx={(theme: Theme) => ({
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  marginY: 8,
                  [theme.breakpoints.up("md")]: {
                    flexDirection: "row",
                    justifyContent: "space-around",
                    alignItems: "flex-start",
                  },
                })}
              >
                <ScheduleDateSelector
                  timezone={timeZone}
                  onSelect={(date: Date) => {
                    // If the selected date has enrollable times, then auto-select the first enrollable time
                    // and mark that we have a time selected. If the date has no enrollable times, then still
                    // select the date and mark that no time is selected.
                    const firstEnrollableTimeForDate =
                      dayjs(
                        enrollableTimesByDate[startOfDateString(date)]?.[0],
                      ).toDate() || null;
                    setSelectedDateTime(firstEnrollableTimeForDate || date);
                    setHasSelectedTime(!!firstEnrollableTimeForDate);
                  }}
                  availableDates={availableDates}
                  selectedDate={selectedDateTime}
                  enrollmentDuration={duration}
                  enrollableTimesByDate={enrollableTimesByDate}
                  hasSelectedTime={hasSelectedTime}
                  futureMeetings={futureMeetings}
                />
                <Box
                  sx={(theme: Theme) => ({
                    [theme.breakpoints.up("md")]: {
                      maxWidth: "40%",
                      marginBottom: 16,
                      paddingBottom: 8,
                    },
                  })}
                >
                  <AvailableTimesGrid
                    timeZone={timeZone}
                    selectedDateTime={selectedDateTime}
                    hasSelectedTime={true}
                    availableTimes={availableTimes}
                    handleTimeChange={startTime => {
                      setSelectedDateTime(startTime);
                      setHasSelectedTime(true);
                    }}
                  />
                </Box>
              </Box>
            </Box>
            {isThreeStepBooking && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <ClassScheduleRequest
                  activity={activity as ClassScheduleRequest_ActivityFragment}
                  user={currentUser!}
                  userTimeZone={timeZone}
                  isLoggedIn={isLoggedIn}
                  renderButton={open => (
                    <Box
                      sx={{
                        display: "flex",
                        marginLeft: 8,
                        py: 8,
                        gap: 4,
                        justifyContent: "flex-start",
                        alignItems: "center",
                        "&:hover": {
                          cursor: "pointer",
                        },
                      }}
                      size="small"
                      variant="ghost"
                      color="primary"
                      onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                        open(e);
                        trackEvent("request_a_time_clicked", {
                          activityUid: activity.uid,
                        });
                      }}
                    >
                      <Icon
                        width={4}
                        height={4}
                        icon={faCalendarCircleExclamation}
                      />
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "primary.main" }}
                      >
                        {t("Request Another Time")}
                      </Typography>
                    </Box>
                  )}
                  onHandleClose={onHandleClose}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: isThreeStepBooking
                  ? "space-between"
                  : "flex-end",
                gap: "0.5em",
                borderTop: "1px solid",
                borderBottom: useStickyFooter ? "none" : "1px solid",
                borderColor: "neutral.light",
                py: useStickyFooter ? "1em" : "0.5em",
                ...(useStickyFooter && {
                  backgroundColor: "white",
                  position: "sticky",
                  bottom: 0,
                  marginBottom: "-1.5em",
                }),
              }}
            >
              {isThreeStepBooking && (
                <EnrollmentDurationSelect
                  enrollmentDuration={duration}
                  setEnrollmentDuration={setEnrollmentDuration}
                  disabled={!hasSelectedTime || futureMeetingsLoading}
                  isPrepayEnrollment={isPrepayEnrollment}
                  isThreeStepBooking={isThreeStepBooking}
                  isRecurringSchedulingAvailable={canScheduleContinuously}
                  durationsWithAvailability={durationsWithAvailability}
                />
              )}

              <Button
                disabled={!hasSelectedTime}
                variant="outlined"
                onClick={weeklyUntilCancel_OnClick}
              >
                {t("Next")}
              </Button>
            </Box>

            {!isThreeStepBooking && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 8,
                  alignItems: "center",
                  p: 8,
                }}
              >
                <Typography variant="inherit">
                  {t("Don’t see what you’re looking for?")}
                </Typography>
                <ClassScheduleRequest
                  activity={activity as ClassScheduleRequest_ActivityFragment}
                  user={currentUser!}
                  userTimeZone={timeZone}
                  isLoggedIn={isLoggedIn}
                  renderButton={open => (
                    <TrackedButton
                      trackingName={
                        "tutoring_available_times_request_another_time"
                      }
                      trackingSharedProperties={{
                        isLoggedIn,
                        activityUid: activity.uid,
                      }}
                      size="small"
                      variant="outlined"
                      onClick={open}
                    >
                      {t("Request Another Time")}
                    </TrackedButton>
                  )}
                  onHandleClose={onHandleClose}
                />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }
  return (
    <Box
      sx={(theme: Theme) => ({
        [theme.breakpoints.up("md")]: {
          display: "flex",
        },
      })}
    >
      <ScheduleDateSelector
        timezone={timeZone}
        onSelect={(date: Date) => {
          setSelectedDateTime(date);
        }}
        availableDates={availableDates}
        selectedDate={selectedDateTime}
      />
      <Box sx={{ margin: "16px", maxWidth: "320px" }}>
        <AvailableTimesGrid
          timeZone={timeZone}
          selectedDateTime={selectedDateTime}
          hasSelectedTime={true}
          availableTimes={availableTimes}
          handleTimeChange={onChange}
        />
      </Box>
    </Box>
  );
}

export function ScheduleDateSelector({
  timezone,
  onSelect,
  selectedDate,
  availableDates = [],
  enrollmentDuration,
  enrollableTimesByDate,
  hasSelectedTime,
  futureMeetings,
}: {
  timezone: string;
  onSelect: (date: Date) => void;
  selectedDate?: Date;
  availableDates?: string[];
  enrollmentDuration?: number | null;
  enrollableTimesByDate?: Record<string, string[]>;
  hasSelectedTime?: boolean;
  futureMeetings?: any;
}) {
  const today = dayjs.tz(timezone).startOf("day").toDate();
  let enrollmentDuration_sessions = null;
  if (enrollmentDuration != null) {
    let count = 0;
    enrollmentDuration_sessions = 0;
    for (const fm of futureMeetings) {
      enrollmentDuration_sessions++;
      if (fm.isAvailable) {
        count++;
      }
      if (count === enrollmentDuration) {
        break;
      }
    }
  }

  return (
    <AvailableDateSelector
      today={today}
      firstAvailableDate={today}
      availableDates={availableDates}
      defaultMonth={selectedDate}
      selectedDate={selectedDate || today}
      timeZone={timezone}
      startOfDateString={(date: Date) =>
        dayjs(date).tz(timezone).startOf("date").toISOString()
      }
      handleDayClick={onSelect}
      numWeeksToRepeat={enrollmentDuration}
      enrollableTimesByDate={enrollableTimesByDate}
      hasSelectedTime={hasSelectedTime}
      enrollmentDuration_sessions={enrollmentDuration_sessions}
    />
  );
}
